import { useQuery } from "@tanstack/react-query";

import { fetchBuildVersion, type BuildVersion } from "@/api/version";

/**
 * Query key for the backend build stamp.
 *
 * NOT persisted. ``lib/queryPersister.ts`` writes every successful query to
 * localStorage by default, and a cached build version is the exact lie this
 * whole surface exists to catch: after a deploy the page would keep showing
 * the PREVIOUS sha, from disk, with total confidence. ``meta.persist: false``
 * is read by ``shouldPersistQuery`` and keeps this one in memory only.
 */
export const buildVersionQueryKey = ["build-version"] as const;

/**
 * useBuildVersionQuery — what build is the web container running?
 *
 * A build stamp cannot change under a running container: the value is baked
 * into the image, so a new sha means a new container and a new page load.
 * ``staleTime: Infinity`` therefore costs nothing in freshness and buys one
 * request per session instead of one per mount.
 *
 * Failure is a normal outcome, not an error state to surface: an older web
 * image has no ``/api/version/`` route and 404s, and the endpoint is
 * deliberately unauthenticated so a logged-out session gets the same answer.
 * ``retry: false`` keeps a 404 from costing three round-trips, and callers
 * render the frontend's own sha alone when this fails — that half is inlined
 * in the bundle and always available.
 */
export const useBuildVersionQuery = ({ enabled = true } = {}) =>
  useQuery<BuildVersion>({
    queryKey: buildVersionQueryKey,
    queryFn: fetchBuildVersion,
    // Callers own the policy of WHO asks — this hook stays auth-agnostic so
    // the endpoint's deliberate no-auth posture is not re-litigated here.
    // ``BuildStamp`` passes ``is_staff``, so a customer session never issues
    // the request at all rather than issuing it and discarding the answer.
    enabled,
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
    refetchOnWindowFocus: false,
    meta: { persist: false },
  });
